// AdminSettings.tsx - Admin management section (SuperAdmin only)
import React, { useState } from 'react';
import { Button, Table, Space, Badge, message, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, StopOutlined, CheckCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { AdminModal } from './modals/AdminModal';

export const AdminSettings: React.FC = () => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data?: any;
  }>({ open: false, mode: 'create' });

  const { users, deleteAdmin, banAdmin, unbanAdmin } = useSuperAdmin();
  const admins = users.filter(user => user.role === 'ADMIN');

  const handleCreate = () => {
    setModalState({ open: true, mode: 'create' });
  };

  const handleEdit = (admin: any) => {
    setModalState({ open: true, mode: 'edit', data: admin });
  };

  const handleDelete = (id: string) => {
    deleteAdmin(id);
    message.success('Admin deleted successfully');
  };

  const handleToggleBan = (admin: any) => {
    if (admin.active) {
      banAdmin(admin.id);
      message.success(`Admin ${admin.username} banned`);
    } else {
      unbanAdmin(admin.id);
      message.success(`Admin ${admin.username} unbanned`);
    }
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: any) => (
        <Badge
          status={record.active ? 'success' : 'error'}
          text={record.active ? 'Active' : 'Banned'}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Button
            icon={record.active ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleBan(record)}
            danger={record.active}
            size="small"
          >
            {record.active ? 'Ban' : 'Unban'}
          </Button>
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Admin
        </Button>
      </div>

      <Table
        dataSource={admins}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <AdminModal
        open={modalState.open}
        mode={modalState.mode}
        initialData={modalState.data}
        onClose={() => setModalState({ open: false, mode: 'create' })}
      />
    </div>
  );
};
